<template>
  <component
    :is="tag"
    ref="container"
    class="navigation__item"
    @keydown.esc="handleEscape"
  >
    <template v-if="tier.links?.length">
      <button
        class="navigation__link"
        :class="[
          {
            [$class('active')]: showMegaNav
          }
        ]"
        type="button"
        @focus="handleHover"
        @keydown.enter.prevent="showMegaNav = !showMegaNav"
        @mouseenter="handleHover"
      >
        <span class="visually-hidden">
          {{
            $string('general.navigation.show_hide_tier', {
              replace: {
                action: showMegaNav ? 'Hide' : 'Show',
                title: tier.title,
              },
            })
          }}
        </span>

        <a
          class="text-body-s-bold"
          aria-hidden="true"
          :href="tier.url"
          v-text="tier.title"
        />
      </button>

      <mega-nav
        v-show="showMegaNav"
        :items="tier.links"
        :mega-nav-footer="megaNavFooter"
      />
    </template>

    <a
      v-else
      class="navigation__link"
      :href="tier.url"
      @focus="closeMegaNavs"
      @mouseenter="closeMegaNavs"
    >
      <span
        class="text-body-s-bold"
        v-text="tier.title"
      />
    </a>
  </component>

  <teleport to="body">
    <transition name="fade">
      <button
        v-if="showMegaNav"
        class="window-overlay window-overlay--meganav"
        tabindex="-1"
        type="button"
        aria-hidden="true"
        @click.prevent="handleEscape"
      />
    </transition>
  </teleport>
</template>

<script>

/**
 * Vue: Navigation link (navigation-link)
 * -----------------------------------------------------------------------------
 * A desktop navigation link with optional mega nav
 *
 * @param {Object} tier - Tier 1 menu item
 * @param {String} [tag] - Name of HTML element tag
 *
 */
import { mapActions } from 'vuex'

import MegaNav from '~global/mega-nav/mega-nav'

export default {
  name: 'NavigationLink',

  components: {
    MegaNav,
  },

  props: {
    tier: {
      type: Object,
      default: () => ({
        title: '',
        items: [],
        url: '',
      }),
    },
    tag: {
      type: String,
      default: 'li',
    },
    megaNavFooter: {
      type: Object,
      default: () => ({
        text: '',
        url: '',
      }),
    },
  },

  data() {
    return {
      showMegaNav: false,
    }
  },

  watch: {

    /**
     * When a mega nav is visible,
     * - Close all other mega navs
     * - Close search drawer
     * - Set focus traps
     */
    async showMegaNav(isVisible) {
      if (isVisible) {
        this.closeMegaNavs(this.tier.handle)
        await this.$nextTick()
        this.setFocusTrap({
          container: this.$refs.container,
        })

        return
      }

      this.clearFocusTrap()
    },
  },

  mounted() {
    cnvs.EventBus.listen('mega-nav:visible', (handle) => {
      if (handle === this.tier.handle || !this.showMegaNav) {
        return
      }
      this.showMegaNav = false
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      setFocusTrap: 'accessibility/setFocusTrap',
    }),

    /**
     * Close all mega navs when toggle is focused
     * @param {String} [excludedId] - handle of a mega nav to exclude from being
     * closed.
     */
    closeMegaNavs(excludedId) {
      cnvs.EventBus.emit('mega-nav:visible', excludedId)
    },

    /**
     * Close mega nav on escape key and focus toggle button
     */
    handleEscape() {
      this.showMegaNav = false
    },

    /**
     * Show mega nav on hover and reset mega nav's current image
     */
    handleHover() {
      this.showMegaNav = true
    },
  },
}
</script>
